import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import MatchResult from "@/components/MatchResult";
import { api, CITIES } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const SKILLS = ["Desain", "Logistik", "Pengajaran", "Legal/Advokasi", "Media/Komunikasi", "Medis/Kesehatan", "Tenaga Lapangan"];
const AVAILABILITY = ["Insidental 1 Hari", "Akhir Pekan", "2-4 Jam/Minggu", "Proyek Jangka Panjang"];
const FOCUS = ["Lingkungan & Sampah", "Pendidikan & Anak", "Kemiskinan & Pangan", "Kesetaraan Sosial & Gender", "Bencana Alam"];

const STEPS = ["Keahlian Utama", "Ketersediaan Waktu", "Fokus Isu Sosial", "Lokasi / Domisili"];

function OptionPill({ selected, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 rounded-2xl border-2 font-bold text-sm transition-colors text-left ${
        selected ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-600 hover:border-emerald-300"
      }`}
    >
      {children}
    </button>
  );
}

export default function MatchmakerPage() {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [skills, setSkills] = useState([]);
  const [availability, setAvailability] = useState("");
  const [focus, setFocus] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const toggleSkill = (s) => setSkills((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  const canNext = [skills.length > 0, !!availability, !!focus, !!city][step];

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/matchmaker", { skills, availability, focus, city });
      setResult(data);
    } catch {
      toast.error("Gagal memproses. Coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  const savePlan = async () => {
    try {
      await api.post("/saved-plans", {
        profile_title: result.profile_title,
        event_titles: result.events.map((e) => e.title),
        user_id: user?.id || "demo-andini",
      });
      toast.success("Rencana Aksi tersimpan di Profil & Dampak Saya.");
    } catch {
      toast.error("Gagal menyimpan rencana.");
    }
  };

  const reset = () => { setResult(null); setStep(0); setSkills([]); setAvailability(""); setFocus(""); setCity(""); };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-32 text-center" data-testid="matchmaker-loading">
        <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto" />
        <h2 className="font-heading font-black text-2xl text-slate-900 mt-6">AI sedang menganalisis profil sipilmu...</h2>
        <p className="text-slate-500 mt-2">Mencocokkan keahlian, waktu, dan fokus isu dengan {"aksi"} paling berdampak.</p>
      </div>
    );
  }

  if (result) {
    return <MatchResult result={result} onSave={savePlan} onReset={reset} />;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
      <span className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-bold px-4 py-1.5 rounded-full">
        <Sparkles className="w-3.5 h-3.5" /> Kenali Dirimu — AI Civic Matchmaker
      </span>
      <h1 className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-tight mt-4">Temukan Peran Sipilmu</h1>
      <p className="text-slate-500 mt-2">4 langkah singkat untuk profil aksi personal berbasis AI.</p>

      <div className="flex gap-2 mt-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full ${i <= step ? "bg-emerald-600" : "bg-slate-200"}`} />
            <p className={`text-[10px] font-bold mt-1.5 uppercase tracking-wide ${i === step ? "text-emerald-700" : "text-slate-400"}`}>{s}</p>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.25 }}
          className="bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-7 mt-8"
          data-testid={`wizard-step-${step + 1}`}
        >
          {step === 0 && (
            <>
              <h2 className="font-heading font-extrabold text-xl text-slate-900 mb-1">Apa keahlian utamamu?</h2>
              <p className="text-sm text-slate-500 mb-5">Pilih satu atau lebih.</p>
              <div className="grid grid-cols-2 gap-3">
                {SKILLS.map((s) => (
                  <OptionPill key={s} testid={`skill-option-${SKILLS.indexOf(s)}`} selected={skills.includes(s)} onClick={() => toggleSkill(s)}>{s}</OptionPill>
                ))}
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <h2 className="font-heading font-extrabold text-xl text-slate-900 mb-5">Berapa banyak waktu yang bisa kamu berikan?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {AVAILABILITY.map((a) => (
                  <OptionPill key={a} testid={`availability-option-${AVAILABILITY.indexOf(a)}`} selected={availability === a} onClick={() => setAvailability(a)}>{a}</OptionPill>
                ))}
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <h2 className="font-heading font-extrabold text-xl text-slate-900 mb-5">Isu sosial apa yang paling kamu pedulikan?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FOCUS.map((f) => (
                  <OptionPill key={f} testid={`focus-option-${FOCUS.indexOf(f)}`} selected={focus === f} onClick={() => setFocus(f)}>{f}</OptionPill>
                ))}
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <h2 className="font-heading font-extrabold text-xl text-slate-900 mb-5">Di mana domisilimu?</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {CITIES.map((c) => (
                  <OptionPill key={c} testid={`city-option-${CITIES.indexOf(c)}`} selected={city === c} onClick={() => setCity(c)}>{c}</OptionPill>
                ))}
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between mt-6">
        <Button data-testid="wizard-back-btn" variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} className="rounded-full font-bold">
          <ArrowLeft className="w-4 h-4 mr-1" /> Kembali
        </Button>
        {step < 3 ? (
          <Button data-testid="wizard-next-btn" disabled={!canNext} onClick={() => setStep(step + 1)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-7">
            Lanjut <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button data-testid="wizard-submit-btn" disabled={!canNext} onClick={submit} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-7">
            <Sparkles className="w-4 h-4 mr-2" /> Buat Profil Aksi Saya
          </Button>
        )}
      </div>
    </div>
  );
}
